<script setup lang="ts">
import { onMounted, reactive, ref, watch } from 'vue'

import { Chart as ChartJS, Title, Tooltip, Legend,
  ArcElement, LineElement, PointElement,
  CategoryScale, LinearScale } from 'chart.js'
import type { Chart, ChartData, ChartDataset, ChartOptions } from 'chart.js'
import { Line, Doughnut } from 'vue-chartjs'
import type { ChartComponentRef } from 'vue-chartjs'

import ChartLegend from '@/components/ChartLegend.vue'
import type { Item as LegendItem } from '@/components/ChartLegend.vue'

ChartJS.register(Title, Tooltip, Legend, ArcElement, LineElement,
  PointElement, CategoryScale, LinearScale)

defineProps<{
  msg: string
}>()

const COLOR_PLATES: string[] = ['#f94144', '#f3722c', '#f8961e', '#f9c74f',
  '#90be6d', '#43aa8b', '#4d908e', '#577590', '#277da1', '#9c6644']
const YEARS: number[] = []
const currentDate = new Date()
const currentYear = currentDate.getFullYear()
for (let i = 0; i < 15; i++) {
  YEARS.push(currentYear - i)
}

const lineChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom'
    }
  }
}
const doughnutChartOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    }
  }
}

const startYear = ref<number>(currentYear)
const endYear = ref<number>(currentYear)
const chartCourseByMonthData = ref<ChartData<'line'>>({
  labels: [],
  datasets: [ { backgroundColor: [], data: [] } ]
})
const chartGenderDistributionLegendItems = ref<LegendItem[]>([])
const chartGenderDistributionData = ref<ChartData<'doughnut'>>({
  labels: [],
  datasets: [ { backgroundColor: [], data: [] } ]
})
const chartTop10DonorLegendItems = ref<LegendItem[]>([])
const chartTop10DonorData = ref<ChartData<'doughnut'>>({
  labels: [],
  datasets: [ { backgroundColor: [], data: [] } ]
})
const chartTop10CountryLegendItems = ref<LegendItem[]>([])
const chartTop10CountryData = ref<ChartData<'doughnut'>>({
  labels: [],
  datasets: [ { backgroundColor: [], data: [] } ]
})

watch([startYear, endYear], () => {
  setChartData(startYear.value, endYear.value)
})

async function setChartData(startYear: number, endYear: number): Promise<void> {
  const result = await fetch('/api/aite/v1/dashboard-data'
    + `?start_year=${startYear}&end_year=${endYear}`)
    .then< {status: boolean, data: Record<string, any> }>(d => d.json())
  const data = result.data
  if (result.status === false || data === undefined) {
    alert('Fetch data failed')
    return
  }
  if (data.course_by_month) {
    const months = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July',
      'August', 'September', 'October', 'November', 'December' ];
    const _data: { [year: string]: ChartDataset<'line'> } = {}
    const yearRange = (endYear - startYear) + 1
    for (let i = 0; i < yearRange; i++) {
      const year =`${startYear + i}` 
      _data[year] = {
        label: year,
        backgroundColor: COLOR_PLATES[i],
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      }
    }
    for (const [ key, value ] of Object.entries(data.course_by_month)) {
      const splitedKey = key.split('-') // e.g. key = 2024-08
      _data[splitedKey[0]].data[Number(splitedKey[1]) - 1] = Number(value)
    }
    chartCourseByMonthData.value = {
      labels: months,
      datasets: Object.values(_data)
    }
  }
  if (data.gender_distribution) {
    const _labels = Object.keys(data.gender_distribution)
    const _backgroundColor: string[] = []
    const _data: number[] = []
    const _legendItems: LegendItem[] = []
    for (let i = 0; i < _labels.length; i++) {
      const color = COLOR_PLATES[i]
      const value = data.gender_distribution[_labels[i]]
      _backgroundColor.push(color)
      _data.push(value)
      _legendItems.push({
        color, label: `${_labels[i]} (${value})`,
      })
    }
    chartGenderDistributionLegendItems.value = _legendItems
    chartGenderDistributionData.value = {
      labels: _labels,
      datasets: [{ backgroundColor: _backgroundColor,
        data: _data }]
    }
  }
  if (data.top_10_donor) {
    const _labels = Object.keys(data.top_10_donor)
    const _backgroundColor: string[] = []
    const _data: number[] = []
    const _legendItems: LegendItem[] = []
    for (let i = 0; i < _labels.length; i++) {
      const color = COLOR_PLATES[i]
      const value = data.top_10_donor[_labels[i]]
      _backgroundColor.push(color)
      _data.push(value)
      _legendItems.push({
        color, label: `${_labels[i]} (${value})`
      })
    }
    chartTop10DonorLegendItems.value = _legendItems
    chartTop10DonorData.value = {
      labels: _labels,
      datasets: [{ backgroundColor: _backgroundColor,
        data: _data }]
    }
  }
  if (data.top_10_country) {
    const _labels = Object.keys(data.top_10_country)
    const _backgroundColor: string[] = []
    const _data: number[] = []
    const _legendItems: LegendItem[] = []
    for (let i = 0; i < _labels.length; i++) {
      const color = COLOR_PLATES[i]
      const value = data.top_10_country[_labels[i]]
      _backgroundColor.push(color)
      _data.push(value)
      _legendItems.push({
        color, label: `${_labels[i]} (${value})`
      })
    }
    chartTop10CountryLegendItems.value = _legendItems
    chartTop10CountryData.value = {
      labels: _labels,
      datasets: [{ backgroundColor: _backgroundColor,
        data: _data }]
    }
  }
}

onMounted(() => {
  setChartData(startYear.value, endYear.value)
})
</script>

<template>
  <div class="row my-3 justify-content-center">
    <div class="col-auto row align-items-center">
      <div class="col-auto">
        <label class="form-label m-0">Start Year</label>
      </div>
      <div class="col-auto">
        <select class="form-select" v-model="startYear">
          <option
            v-for="year in YEARS"
            :key="year"
            :value="year"
          >{{ year }}</option>
        </select>
      </div>
    </div>
    <div class="col-auto row align-items-center">
      <div class="col-auto">
        <label class="form-label m-0">End Year</label>
      </div>
      <div class="col-auto">
        <select class="form-select" v-model="endYear">
          <option
            v-for="year in YEARS"
            :key="year"
            :value="year"
          >{{ year }}</option>
        </select>
      </div>
    </div>
  </div>
  <div class="d-flex">
    <div class="card w-100">
      <dvv class="card-header">Courses By Month</dvv>
      <div class="car-body">
        <Line
          id="chartCourseByMonth"
          class="container-chart"
          :options="lineChartOptions"
          :data="chartCourseByMonthData"
        />
      </div>
    </div>
  </div>
  <div class="d-flex flex-row">
    <div class="card" style="width: 33.33%">
      <dvv class="card-header">Gender Distribution</dvv>
      <div class="car-body d-flex flex-column gap-3">
        <div class="container-chart">
          <Doughnut
            ref="chartGenderDistributionRef"
            id="chartGenderDistribution"
            :options="doughnutChartOptions"
            :data="chartGenderDistributionData"
          />
        </div>
        <ChartLegend :items="chartGenderDistributionLegendItems" />
      </div>
    </div>
    <div class="card" style="width: 33.33%">
      <dvv class="card-header">Top 10 Donors</dvv>
      <div class="car-body d-flex flex-column gap-3">
        <div class="container-chart">
          <Doughnut
            id="chartTop10Donor"
            :options="doughnutChartOptions"
            :data="chartTop10DonorData"
          />
        </div>
        <ChartLegend :items="chartTop10DonorLegendItems" />
      </div>
    </div>
    <div class="card" style="width: 33.33%">
      <dvv class="card-header">Top 10 Countries</dvv>
      <div class="car-body d-flex flex-column gap-3">
        <div class="container-chart">
          <Doughnut
            id="chartTop10Country"
            :options="doughnutChartOptions"
            :data="chartTop10CountryData"
          />
        </div>
        <ChartLegend :items="chartTop10CountryLegendItems" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.container-chart {
  /* width: 500px; */
  height: 450px;
}
</style>
